import React, { useEffect, useState } from "react";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useLocation, Link } from "react-router-dom";

import PersonIcon from "@mui/icons-material/Person";
import EmailIcon from "@mui/icons-material/Email";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";

import { setUser, showToast } from "../../store/actions";
import AuthService from "../../service/auth";
import Loading from "../Loading";

import { regexEmail, regexPassword } from "../../util/regularExpress";
import { PasswordValidationText } from "../Common/Common";

export default function SignUp() {
  const [errors, setErrors] = useState({
    loginId: false,
    firstName: false,
    lastName: false,
    email: false,
    password: false,
    confirmPassword: false,
  });

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user } = useSelector((state) => state);

  const location = useLocation();

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);

    if (queryParams.has("message") && queryParams.get("message")) {
      const message = queryParams.get("message");
      dispatch(
        showToast({
          message,
          type: "error",
        })
      );

      queryParams.delete("message");
      navigate("/");
    }
  }, [navigate, dispatch, location.search]);

  const [loginId, setLoginId] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  // Check if user is Logged in then redirecting to Dashboard
  useEffect(() => {
    const getAuthUser = async () => {
      const authService = new AuthService();
      const response = await authService.getAuthUser();

      if (response.success) {
        dispatch(setUser(response.data));
        navigate("/dashboard");
      } else {
        dispatch(setUser(null));
      }
    };

    getAuthUser();
    if (user.currentUser) {
      navigate("/dashboard");
    }

    return () => {
      setLoginId("");
      setFirstName("");
      setLastName("");
      setEmail("");
      setPassword("");
      setConfirmPassword("");
    };
  }, [user.currentUser, navigate, dispatch]);

  const handleSubmit = async (event) => {
    event.preventDefault();

    setErrors({
      loginId: loginId.trim().length < 3 || loginId.trim().length > 20,
      firstName: firstName.trim().length < 3 || firstName.trim().length > 50,
      lastName: lastName.trim().length < 3 || lastName.trim().length > 50,
      email: email.trim().length > 100 || !regexEmail(email.trim()),
      password: !regexPassword(password.trim()),
      confirmPassword: password.trim() !== confirmPassword.trim(),
    });

    if (
      !(
        loginId.trim().length < 3 ||
        loginId.trim().length > 20 ||
        firstName.trim().length < 3 ||
        firstName.trim().length > 50 ||
        lastName.trim().length < 3 ||
        lastName.trim().length > 50 ||
        email.trim().length > 100 ||
        !regexEmail(email.trim()) ||
        !regexPassword(password.trim()) ||
        password.trim() !== confirmPassword.trim()
      )
    ) {
      setLoading(true);

      const authService = new AuthService();
      const response = await authService.signup({
        LoginId: loginId.trim(),
        FirstName: firstName.trim(),
        LastName: lastName.trim(),
        Email: email.trim(),
        Password: password.trim(),
      });

      if (response.success) {
        dispatch(
          showToast({
            message: response.message,
            type: "success",
          })
        );

        setLoginId("");
        setFirstName("");
        setLastName("");
        setEmail("");
        setPassword("");
        setConfirmPassword("");

        navigate("/");
      }

      setLoading(false);
    }
  };

  if (user.isLoading) return <Loading />;

  return (
    <Box
      component="form"
      onSubmit={handleSubmit}
      noValidate
      style={{ padding: "25px" }}
    >
      <Typography style={{ fontSize: "18px", fontWeight: "bold" }}>Register Form</Typography>

      <TextField
        margin="normal"
        value={loginId}
        onChange={(e) => setLoginId(e.target.value)}
        required
        fullWidth
        label="Login ID"
        autoFocus
        error={errors.loginId}
        helperText={errors.loginId ? "Login Id length should be between 3 and 20!" : ""}
        sx={{ mt: 4 }}
        InputProps={{
          endAdornment: <PersonIcon color="primary" />,
        }}
      />

      <Box display={"flex"} justifyContent={"space-between"}>
        <TextField
          margin="normal"
          value={firstName}
          onChange={(e) => setFirstName(e.target.value)}
          required
          fullWidth
          label="First Name"
          autoFocus
          error={errors.firstName}
          helperText={errors.firstName ? "First Name length should be between 3 and 50!" : ""}
          InputProps={{
            endAdornment: <PersonIcon color="primary" />,
          }}
        />

        <TextField
          margin="normal"
          value={lastName}
          onChange={(e) => setLastName(e.target.value)}
          sx={{ ml: 2 }}
          required
          fullWidth
          label="Last name"
          autoFocus
          error={errors.lastName}
          helperText={errors.lastName ? "Last Name length should be between 3 and 50!" : ""}
          InputProps={{
            endAdornment: <PersonIcon color="primary" />,
          }}
        />
      </Box>

      <TextField
        margin="normal"
        type="email"
        required
        variant="outlined"
        fullWidth
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        label="Email"
        error={errors.email}
        helperText={errors.email ? "Please enter an valid email." : ""}
        InputProps={{
          endAdornment: <EmailIcon color="primary" />,
        }}
      />

      <Box display={"flex"} justifyContent={"space-between"}>
        <TextField
          margin="normal"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
          fullWidth
          label="Password"
          type={showPassword ? "text" : "password"}
          autoComplete="new-password"
          placeholder="******"
          error={errors.password}
          helperText={errors.password ? <PasswordValidationText /> : ""}
          InputProps={{
            endAdornment: (
              <IconButton
                aria-label="toggle password visibility"
                onClick={() => setShowPassword((prev) => !prev)}
                onMouseDown={(e) => e.preventDefault()}
                edge="end"
              >
                {showPassword ? <VisibilityOff /> : <Visibility />}
              </IconButton>
            ),
          }}
        />

        <TextField
          margin="normal"
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          sx={{ ml: 2 }}
          required
          fullWidth
          label="Confirm Password"
          type={showConfirmPassword ? "text" : "password"}
          autoComplete="confirm-password"
          placeholder="******"
          error={errors.confirmPassword}
          helperText={errors.confirmPassword ? "Passwords does not match" : ""}
          InputProps={{
            endAdornment: (
              <IconButton
                aria-label="toggle confirm password visibility"
                onClick={() => setShowConfirmPassword((prev) => !prev)}
                onMouseDown={(e) => e.preventDefault()}
                edge="end"
              >
                {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
              </IconButton>
            ),
          }}
        />
      </Box>

      <Button
        className="btn btn-primary"
        color="secondary"
        type="submit"
        fullWidth
        variant="contained"
        sx={{ mt: 2, mb: 0 }}
        disabled={!(loginId && firstName && lastName && password && confirmPassword) || loading}
      >
        {loading ? <CircularProgress style={{ width: "20px", height: "20px" }} /> : "Sign Up"}
      </Button>

      <Box display={"flex"} justifyContent={"center"} sx={{ mt: 3 }}>
        <span
          style={{
            fontSize: "14px",
            textAlign: "center",
          }}
        >
          We supply to promotional & marketing companies only. <br />
          No end user or companies aligned with supplies will be approved.
        </span>
      </Box>
    </Box>
  );
}
